import { useMemo, useState } from 'react';

/**
 * Small hand-rolled SVG line chart for the admin dashboard (GHS5-99).
 *
 * Deliberately not a charting library: [AdminPage] is imported eagerly by
 * routes.tsx, so anything added here lands in the public site's main bundle.
 * The shape needed is narrow (a handful of series over a shared categorical
 * axis), so ~200 lines of SVG beats ~100 KB gzipped of Recharts.
 *
 * Scaling is done by viewBox, so the chart is responsive without measuring the
 * DOM. Hover is driven by one invisible full-height hit rect per x slot rather
 * than by pointer coordinates, which keeps it correct under that scaling.
 *
 * A null `y` is a gap, NOT a zero — the polyline breaks there. That's how a
 * week in progress renders: its remaining days simply aren't drawn yet.
 */

export interface ChartPoint {
  y: number | null;
  /** Row label inside the tooltip, e.g. the actual date for this series. */
  sub?: string;
}

export interface ChartSeries {
  key: string;
  label: string;
  color: string;
  points: ChartPoint[];
}

interface LineChartProps {
  series: ChartSeries[];
  /** One label per x slot. Length defines the axis. */
  xLabels: string[];
  /** Unit shown in the tooltip header, e.g. 'runs'. */
  valueLabel: string;
  /** Accessible summary; the same data is available in the Table view. */
  ariaLabel: string;
}

const VIEW_W = 760;
const VIEW_H = 260;
const PAD_LEFT = 52;
const PAD_RIGHT = 14;
const PAD_TOP = 14;
const PAD_BOTTOM = 30;

const PLOT_W = VIEW_W - PAD_LEFT - PAD_RIGHT;
const PLOT_H = VIEW_H - PAD_TOP - PAD_BOTTOM;

const nf = new Intl.NumberFormat('en-US');

/** Round a raw axis step up to a 1 / 2 / 5 × 10ⁿ value so tick labels stay readable. */
function niceStep(raw: number): number {
  if (raw <= 0) return 1;
  const magnitude = 10 ** Math.floor(Math.log10(raw));
  const normalized = raw / magnitude;
  const step = normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10;
  return step * magnitude;
}

function axisTicks(max: number): number[] {
  if (max <= 0) return [0, 1];
  const step = niceStep(max / 4);
  const top = Math.ceil(max / step) * step;
  const ticks: number[] = [];
  for (let v = 0; v <= top + step / 2; v += step) ticks.push(v);
  return ticks;
}

function xAt(index: number, count: number): number {
  if (count <= 1) return PAD_LEFT + PLOT_W / 2;
  return PAD_LEFT + (index * PLOT_W) / (count - 1);
}

export function LineChart({ series, xLabels, valueLabel, ariaLabel }: LineChartProps) {
  const [hidden, setHidden] = useState<Set<string>>(new Set());
  const [active, setActive] = useState<number | null>(null);

  const visible = series.filter((s) => !hidden.has(s.key));

  const ticks = useMemo(() => {
    const max = Math.max(
      0,
      ...visible.flatMap((s) => s.points.map((p) => (p.y == null ? 0 : p.y))),
    );
    return axisTicks(max);
  }, [visible]);

  const axisMax = ticks[ticks.length - 1] ?? 1;
  const yAt = (value: number): number => PAD_TOP + PLOT_H - (value / axisMax) * PLOT_H;

  const toggle = (key: string): void => {
    setHidden((prev) => {
      const next = new Set(prev);
      // Never let the last visible series be hidden — an empty plot reads as
      // "no data" rather than "you filtered everything out".
      if (next.has(key)) next.delete(key);
      else if (series.length - next.size > 1) next.add(key);
      return next;
    });
  };

  const hasAnyPoint = series.some((s) => s.points.some((p) => p.y != null));

  if (xLabels.length === 0 || !hasAnyPoint) {
    return <p className="text-white/50">No play recorded in this window yet.</p>;
  }

  const activeRows =
    active == null
      ? []
      : visible
          .map((s) => ({ series: s, point: s.points[active] }))
          .filter((r): r is { series: ChartSeries; point: ChartPoint } => r.point?.y != null);

  return (
    <div className="flex flex-col gap-3">
      <div className="relative">
        <svg
          viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
          className="h-auto w-full overflow-visible"
          role="img"
          aria-label={ariaLabel}
          onMouseLeave={() => setActive(null)}
        >
          {/* Gridlines + y-axis labels */}
          {ticks.map((t) => (
            <g key={t}>
              <line
                x1={PAD_LEFT}
                x2={PAD_LEFT + PLOT_W}
                y1={yAt(t)}
                y2={yAt(t)}
                stroke="currentColor"
                strokeWidth={1}
                className="text-white/10"
              />
              <text
                x={PAD_LEFT - 10}
                y={yAt(t)}
                textAnchor="end"
                dominantBaseline="middle"
                className="fill-white/45 text-[11px]"
              >
                {nf.format(t)}
              </text>
            </g>
          ))}

          {/* x-axis labels */}
          {xLabels.map((label, i) => (
            <text
              key={`${label}-${i}`}
              x={xAt(i, xLabels.length)}
              y={VIEW_H - 10}
              textAnchor="middle"
              className={
                i === active ? 'fill-white text-[11px]' : 'fill-white/45 text-[11px]'
              }
            >
              {label}
            </text>
          ))}

          {/* Hovered column marker */}
          {active != null && (
            <line
              x1={xAt(active, xLabels.length)}
              x2={xAt(active, xLabels.length)}
              y1={PAD_TOP}
              y2={PAD_TOP + PLOT_H}
              stroke="currentColor"
              strokeWidth={1}
              className="text-white/25"
            />
          )}

          {/* Series: each run of consecutive non-null points is its own polyline
              so a gap (a day that hasn't happened) isn't bridged. */}
          {visible.map((s) => {
            const runs: Array<Array<{ x: number; y: number }>> = [];
            let current: Array<{ x: number; y: number }> = [];
            s.points.forEach((p, i) => {
              if (p.y == null) {
                if (current.length) runs.push(current);
                current = [];
                return;
              }
              current.push({ x: xAt(i, xLabels.length), y: yAt(p.y) });
            });
            if (current.length) runs.push(current);

            return (
              <g key={s.key}>
                {runs.map((run, ri) => (
                  <polyline
                    key={ri}
                    points={run.map((pt) => `${pt.x},${pt.y}`).join(' ')}
                    fill="none"
                    stroke={s.color}
                    strokeWidth={2}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                ))}
                {runs.flat().map((pt, pi) => (
                  <circle key={pi} cx={pt.x} cy={pt.y} r={2.5} fill={s.color} />
                ))}
              </g>
            );
          })}

          {/* Hit areas: one column per x slot, full plot height. */}
          {xLabels.map((label, i) => {
            const slot = xLabels.length > 1 ? PLOT_W / (xLabels.length - 1) : PLOT_W;
            return (
              <rect
                key={`hit-${label}-${i}`}
                x={xAt(i, xLabels.length) - slot / 2}
                y={PAD_TOP}
                width={slot}
                height={PLOT_H}
                fill="transparent"
                onMouseEnter={() => setActive(i)}
              />
            );
          })}
        </svg>

        {active != null && activeRows.length > 0 && (
          <div
            className="pointer-events-none absolute top-2 z-10 min-w-[9rem] -translate-x-1/2 rounded-md border border-white/15 bg-black/90 px-3 py-2 text-xs shadow-lg"
            style={{
              left: `${Math.min(88, Math.max(12, (xAt(active, xLabels.length) / VIEW_W) * 100))}%`,
            }}
          >
            <div className="mb-1 font-heading uppercase tracking-wide text-white/55">
              {xLabels[active]} · {valueLabel}
            </div>
            {activeRows.map(({ series: s, point }) => (
              <div key={s.key} className="flex items-center justify-between gap-3 py-0.5">
                <span className="flex items-center gap-1.5 text-white/70">
                  <span
                    className="inline-block h-2 w-2 rounded-full"
                    style={{ backgroundColor: s.color }}
                  />
                  {point.sub ?? s.label}
                </span>
                <span className="font-heading text-white">{nf.format(point.y as number)}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {series.length > 1 && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          {series.map((s) => {
            const off = hidden.has(s.key);
            return (
              <button
                key={s.key}
                type="button"
                aria-pressed={!off}
                onClick={() => toggle(s.key)}
                className={[
                  'flex items-center gap-1.5 text-xs transition focus:outline-none focus-visible:ring-2 focus-visible:ring-white',
                  off ? 'text-white/30' : 'text-white/75 hover:text-white',
                ].join(' ')}
              >
                <span
                  className="inline-block h-2.5 w-2.5 rounded-full"
                  style={{ backgroundColor: off ? 'transparent' : s.color, boxShadow: `inset 0 0 0 2px ${s.color}` }}
                />
                {s.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
